import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Download, PictureAsPdf, Upload } from '@mui/icons-material'
import { Box, Button, FormHelperText, Skeleton, Stack, Typography } from '@mui/material'

import { useDownloadInternalReportFileMutation, useUploadInternalReportFileMutation } from '../../../../api/reports'
import { MAX_PUBLIC_UPLOAD_FILE_SIZE_BYTES, MAX_PUBLIC_UPLOAD_FILE_SIZE_MB } from '../../../../helpers/fileUploadLimits'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { useTrackReportUpload } from '../../helpers/reportUploads'

type ReportFileFieldProps = {
  /** The uploaded file's id; '' when there is none. */
  value: string
  onChange: (fileId: string) => void
  /** The stored report and its file, which can be downloaded while it's still the selected one. */
  storedReport?: { id: string; fileId: string }
  error?: boolean
  disabled?: boolean
}

export const isPdf = (file: File) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')

// "%PDF-"
const pdfSignature = [0x25, 0x50, 0x44, 0x46, 0x2d]

// FileReader rather than `Blob.arrayBuffer()`, which jsdom doesn't have.
export const hasPdfSignature = (file: Blob) =>
  new Promise<boolean>((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      const bytes = new Uint8Array(reader.result as ArrayBuffer)
      resolve(pdfSignature.every((byte, index) => bytes[index] === byte))
    }
    reader.onerror = () => resolve(false)
    reader.readAsArrayBuffer(file.slice(0, pdfSignature.length))
  })

export const ReportFileField: FC<ReportFileFieldProps> = ({ value, onChange, storedReport, error, disabled }) => {
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const [uploadFile] = useUploadInternalReportFileMutation()
  const [downloadFile, { isLoading: isDownloading }] = useDownloadInternalReportFileMutation()
  const [uploadedName, setUploadedName] = useState<{ fileId: string; name: string }>()
  // Covers reading the file's signature as well as the upload.
  const [isUploading, setIsUploading] = useState(false)
  useTrackReportUpload('fileId', isUploading)

  const upload = async (file?: File) => {
    if (!file) return
    if (!isPdf(file)) return notifyError(t('internal-analysts:reports_file_not_pdf'))
    if (file.size >= MAX_PUBLIC_UPLOAD_FILE_SIZE_BYTES)
      return notifyError(t('internal-analysts:reports_file_too_large', { size: MAX_PUBLIC_UPLOAD_FILE_SIZE_MB }))
    setIsUploading(true)
    try {
      if (!(await hasPdfSignature(file))) return notifyError(t('internal-analysts:reports_file_not_pdf'))
      const stored = await uploadFile(file).unwrap()
      if (!stored?.id) throw new Error('No file id')
      setUploadedName({ fileId: stored.id, name: stored.originalName || file.name })
      onChange(stored.id)
      notifySuccess(t('internal-analysts:reports_file_uploaded'))
    } catch {
      notifyError(t('internal-analysts:reports_file_upload_failed'))
    } finally {
      setIsUploading(false)
    }
  }

  const download = async () => {
    if (!storedReport) return
    await run(downloadFile(storedReport.id).unwrap(), { error: t('internal-analysts:reports_download_failed') })
  }

  const isStoredFile = !!storedReport && !!value && value === storedReport.fileId
  const fileLabel = !value
    ? t('internal-analysts:reports_file_none')
    : uploadedName?.fileId === value
    ? uploadedName.name
    : isStoredFile
    ? t('internal-analysts:reports_file_stored')
    : value

  return (
    <Box>
      <Typography variant="body2" fontWeight="bold" mb={1}>
        {t('internal-analysts:reports_file')} *
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        {isUploading ? (
          <Skeleton variant="rounded" width={240} height={32} />
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <PictureAsPdf color={value ? 'primary' : 'disabled'} />
            <Typography variant="body2" color={value ? 'text.primary' : 'text.secondary'}>
              {fileLabel}
            </Typography>
          </Stack>
        )}
        <Button component="label" variant="outlined" startIcon={<Upload />} disabled={disabled || isUploading}>
          {value ? t('internal-analysts:reports_file_replace') : t('internal-analysts:reports_file_select')}
          <input
            hidden
            type="file"
            accept="application/pdf,.pdf"
            aria-label={t('internal-analysts:reports_file')}
            onChange={(event) => {
              upload(event.target.files?.[0])
              event.target.value = ''
            }}
          />
        </Button>
        {isStoredFile && (
          <Button startIcon={<Download />} onClick={download} disabled={isDownloading}>
            {t('internal-analysts:reports_download')}
          </Button>
        )}
      </Stack>
      <FormHelperText error={error}>
        {error ? t('internal-analysts:reports_file_required') : t('internal-analysts:reports_file_help', { size: MAX_PUBLIC_UPLOAD_FILE_SIZE_MB })}
      </FormHelperText>
    </Box>
  )
}
